/**
 * Check Form Template Targeting Configuration
 *
 * Constants and helpers for the check form targeting UI.
 * These must match the backend constraints and database values.
 */

export const CHECK_CATEGORIES = [
  { value: 'robot_check', label: 'Robot Check', description: 'Checks performed on robots (brush or spray)' },
  { value: 'equipment_check', label: 'Equipment Check', description: 'Checks performed on equipment (vans, toolboxes, hired kit)' },
  { value: 'generic_check', label: 'Generic Check', description: 'Other general checks' },
  // Gareth 2026-08-04: generalised from "Sales Form" — these attach to ANY
  // logged activity (sales steps, operational procedures) or pipeline stages.
  // The stored value stays 'sales_form' so existing templates keep resolving.
  { value: 'sales_form', label: 'Activity Form', description: 'Forms attached when logging an activity (sales steps, procedures with specific steps) or triggered by pipeline stages' }
];

// Fallback/default work log type metadata only. The real source of truth is the
// Scheduled & Ticketed Support "work_log_types" config, served live at
// GET /api/v1/support/work-log-config. CheckFormSettings fetches that endpoint and
// falls back to this list only if the request fails or returns no active types, so
// admins can target templates to custom work log types (e.g. "installation")
// without a code change. Do not treat this constant as the current configured list.
// Scheduled maintenance completion is not a work log type (it has its own
// completion flow), but it is a targetable context: templates targeting this
// value are presented when signing off a scheduled maintenance task. A
// template is EITHER for reactive/ticketed work (work-log-type targeting)
// OR for scheduled maintenance (visit type + service year targeting) - never
// both, so reactive forms cannot leak into maintenance sign-offs.
export const SCHEDULED_MAINTENANCE_WORK_CONTEXT = {
  value: 'scheduled_maintenance',
  label: 'Scheduled Maintenance',
  description: 'Scheduled maintenance sign-off (servicing inspection / annual inspection)'
};

export function isScheduledMaintenanceTemplate(targetWorkLogTypes) {
  return Array.isArray(targetWorkLogTypes) && targetWorkLogTypes.includes(SCHEDULED_MAINTENANCE_WORK_CONTEXT.value);
}

// Scheduled Work job types that are maintenance visits, and the visit type
// their check form targets. Log Work on these jobs looks for the scheduled
// maintenance form for that visit first (Gareth 2026-09-15).
const MAINTENANCE_TYPE_BY_TASK_TYPE = {
  quarterly_health_check: 'health_check',
  annual_service: 'annual_service'
};

export function maintenanceTypeForTaskType(taskType) {
  return MAINTENANCE_TYPE_BY_TASK_TYPE[taskType] || null;
}

// Strips the scheduled-maintenance context from a work-log-type option list
// so reactive pickers never offer it (it is selected via the Usage toggle).
export function reactiveWorkLogTypeOptions(list) {
  return (Array.isArray(list) ? list : []).filter(t => t.value !== SCHEDULED_MAINTENANCE_WORK_CONTEXT.value);
}

// Brush/Spray assets resolve robot_check templates; every other asset type
// resolves generic_check - shared by ticket, task, and maintenance flows.
export function checkCategoryForAssetType(robotType) {
  if (!robotType || typeof robotType !== 'string') return null;
  const lower = robotType.toLowerCase();
  return (lower.includes('brush') || lower.includes('spray')) ? 'robot_check' : 'generic_check';
}

export const DEFAULT_WORK_LOG_TYPES = [
  { value: 'site_work', label: 'Site Work', description: 'On-site maintenance or service' },
  { value: 'remote_session', label: 'Remote Session', description: 'Remote support session' },
  { value: 'phone_call', label: 'Phone Call', description: 'Phone support call' },
  { value: 'whatsapp', label: 'WhatsApp', description: 'WhatsApp communication' },
  { value: 'diagnosis', label: 'Diagnosis', description: 'Fault diagnosis work' },
  { value: 'parts_fit', label: 'Parts Fit', description: 'Parts fitting work' },
  { value: 'handover_note', label: 'Handover Note', description: 'Handover documentation' },
  { value: 'customer_update', label: 'Customer Update', description: 'Customer status update' }
];

export const ASSET_TYPE_GROUPS = [
  {
    label: 'Brush',
    types: [
      { value: 'pre_brush', label: 'Pre-Brush' },
      { value: 'dual_pre_brush', label: 'Dual Pre-Brush' }
    ]
  },
  {
    label: 'Spray',
    types: [
      { value: 'post_spray', label: 'Post-Spray' },
      { value: 'pre_spray', label: 'Pre-Spray' },
      { value: 'dual_post_spray', label: 'Dual Post-Spray' },
      { value: 'dual_pre_spray', label: 'Dual Pre-Spray' }
    ]
  },
  {
    label: 'Other',
    types: [
      { value: 'bci_parlour', label: 'BCI Parlour' },
      { value: 'bci_race', label: 'BCI Race' },
      { value: 'ids_vision', label: 'Barn Vision' },
      { value: 'id_verify', label: 'ID Verify' },
      { value: 'collar', label: 'Collar' },
      { value: 'i_qube', label: 'i-Qube' },
      { value: 'general_project', label: 'General Project' }
    ]
  }
];

export const ALL_ASSET_TYPES = ASSET_TYPE_GROUPS.flatMap(group => group.types);

// Maintenance visit types a template can target. Consulted at maintenance
// sign-off, and in Log Work on Quarterly Health Check (health_check) and
// Annual Service (annual_service) jobs.
export const MAINTENANCE_VISIT_TYPES = [
  { value: 'health_check', label: 'Servicing Inspection', description: 'Quarterly inspections (robots) and servicing-type cadence visits' },
  { value: 'annual_service', label: 'Annual Inspection', description: 'Annual services (robots) and annual-type cadence visits' },
  { value: 'custom_service', label: 'Custom Service', description: 'Custom cadence visits' }
];

// Robot contracts run 5 years, so annual inspections can differ per year.
export const SERVICE_YEARS = [1, 2, 3, 4, 5];

// Service years only apply to annual inspections: the years picker shows
// while the visit types are "all" or include them, and when it is hidden no
// years may be saved (they would silently narrow the form to those years).
export function serviceYearsTargetingApplies(maintenanceTypesMode, maintenanceTypes) {
  return maintenanceTypesMode === 'all' || (maintenanceTypes || []).includes('annual_service');
}

export function getMaintenanceTypeLabel(value) {
  const type = MAINTENANCE_VISIT_TYPES.find(t => t.value === value);
  return type?.label || value || 'Unknown';
}

// Build asset type groups from the live asset config (admin-managed types in
// Settings -> Asset Config). Known brush/spray values keep their groups;
// every other active type - including admin-created ones - lands in "Other".
// Falls back to the static groups when no config list is supplied.
export function buildAssetTypeGroups(configAssetTypes) {
  if (!Array.isArray(configAssetTypes) || configAssetTypes.length === 0) {
    return ASSET_TYPE_GROUPS;
  }

  const brushValues = new Set(['pre_brush', 'dual_pre_brush']);
  const sprayValues = new Set(['post_spray', 'pre_spray', 'dual_post_spray', 'dual_pre_spray']);

  const brush = [];
  const spray = [];
  const other = [];
  for (const type of configAssetTypes) {
    if (!type || !type.value || type.active === false) continue;
    const option = { value: type.value, label: type.label || type.value };
    if (brushValues.has(type.value)) brush.push(option);
    else if (sprayValues.has(type.value)) spray.push(option);
    else other.push(option);
  }

  const groups = [];
  if (brush.length > 0) groups.push({ label: 'Brush', types: brush });
  if (spray.length > 0) groups.push({ label: 'Spray', types: spray });
  if (other.length > 0) groups.push({ label: 'Other', types: other });
  return groups.length > 0 ? groups : ASSET_TYPE_GROUPS;
}

export function getCategoryLabel(value) {
  const category = CHECK_CATEGORIES.find(c => c.value === value);
  return category?.label || value || 'Unknown';
}

// Pass the live-configured list (from /support/work-log-config) when available;
// falls back to DEFAULT_WORK_LOG_TYPES, and to the raw value if not found in either
// (e.g. a stale value from a work log type that's since been renamed/deactivated).
export function getWorkLogTypeLabel(value, list = DEFAULT_WORK_LOG_TYPES) {
  if (value === SCHEDULED_MAINTENANCE_WORK_CONTEXT.value) {
    return SCHEDULED_MAINTENANCE_WORK_CONTEXT.label;
  }
  const type = list.find(t => t.value === value) || DEFAULT_WORK_LOG_TYPES.find(t => t.value === value);
  return type?.label || value || 'Unknown';
}

export function getAssetTypeLabel(value) {
  const type = ALL_ASSET_TYPES.find(t => t.value === value);
  return type?.label || value || 'Unknown';
}

// Equipment types are config-driven (GET /check-form-templates/equipment-types);
// this fallback only covers the seeded defaults.
export const DEFAULT_EQUIPMENT_TYPES = [
  { value: 'van', label: 'Van' },
  { value: 'toolbox', label: 'Toolbox' },
  { value: 'cherry_picker', label: 'Cherry Picker' }
];

export function getEquipmentTypeLabel(value, list = DEFAULT_EQUIPMENT_TYPES) {
  const type = (Array.isArray(list) ? list : []).find(t => t.value === value)
    || DEFAULT_EQUIPMENT_TYPES.find(t => t.value === value);
  return type?.label || value || 'Unknown';
}

export function formatTargetingDisplay(template, workLogTypeList = DEFAULT_WORK_LOG_TYPES, equipmentTypeList = DEFAULT_EQUIPMENT_TYPES) {
  const parts = [];

  if (template.check_category) {
    parts.push(`Category: ${getCategoryLabel(template.check_category)}`);
  }

  if (isScheduledMaintenanceTemplate(template.target_work_log_types)) {
    parts.push('Usage: Scheduled Maintenance');
  } else if (template.target_work_log_types === null) {
    parts.push('Work logs: All (reactive)');
  } else if (Array.isArray(template.target_work_log_types)) {
    const labels = template.target_work_log_types.map(v => getWorkLogTypeLabel(v, workLogTypeList));
    parts.push(`Work logs: ${labels.join(', ')}`);
  }

  if (template.target_asset_types === null) {
    parts.push('Assets: All / Not asset-specific');
  } else if (Array.isArray(template.target_asset_types)) {
    const labels = template.target_asset_types.map(getAssetTypeLabel);
    parts.push(`Assets: ${labels.join(', ')}`);
  }

  if (Array.isArray(template.target_maintenance_types)) {
    const labels = template.target_maintenance_types.map(getMaintenanceTypeLabel);
    parts.push(`Maintenance visits: ${labels.join(', ')}`);
  }

  if (Array.isArray(template.target_service_years)) {
    parts.push(`Service years: ${template.target_service_years.join(', ')}`);
  }

  if (Array.isArray(template.target_equipment_types)) {
    const labels = template.target_equipment_types.map(v => getEquipmentTypeLabel(v, equipmentTypeList));
    parts.push(`Equipment: ${labels.join(', ')}`);
  }

  return parts;
}
